import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router';
import { useOrgScope, writeLastOrgSlug, writeLastWorkspaceSlug } from '@/entities/org';
import { CreateWorkspaceButton } from '@/features/workspace/create-workspace';
import { EmptyState } from '@/shared/ui/empty-state';
import { ErrorState } from '@/shared/ui/error-state';
import { PageLayout } from '@/shared/ui/page-layout';
import { Skeleton } from '@/shared/ui/skeleton';
import { NotFoundPage } from '@/widgets/not-found';

function ScopeLoading() {
  const { t } = useTranslation('org');
  return (
    <PageLayout title={<Skeleton className="h-9 w-64" />}>
      <section aria-busy="true" aria-label={t('home.loading')} className="grid gap-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </section>
    </PageLayout>
  );
}

function ScopeError({ message, retry }: { message: string; retry: () => void }) {
  const { t } = useTranslation('org');
  return (
    <PageLayout title={t('home.errorTitle')}>
      <ErrorState title={t('home.errorTitle')} description={message} onRetry={retry} />
    </PageLayout>
  );
}

/**
 * Gate for `/o/:orgSlug/*`: resolves the org by slug through RLS. A slug the person cannot see gets the
 * standard not-found state. Pages below read `useOrgContext()`.
 */
export function RequireOrg() {
  const scope = useOrgScope();
  const slug = scope.status === 'ready' && !scope.notFound ? (scope.org?.slug ?? null) : null;
  useEffect(() => {
    if (slug) writeLastOrgSlug(slug);
  }, [slug]);
  if (scope.status === 'loading') return <ScopeLoading />;
  if (scope.status === 'error')
    return <ScopeError message={scope.error.message} retry={scope.retry} />;
  if (scope.notFound || !scope.org || !scope.role) return <NotFoundPage />;
  return <Outlet />;
}

/**
 * Gate for `/o/:orgSlug/w/:workspaceSlug/*`: the workspace must be one the person can see in this org. The
 * one opened is remembered per org.
 */
export function RequireWorkspace() {
  const scope = useOrgScope();
  const { workspaceSlug = '' } = useParams();
  const ready = scope.status === 'ready';
  const workspace = ready ? scope.workspaceBySlug(workspaceSlug) : null;
  const orgSlug = ready ? (scope.org?.slug ?? null) : null;
  // for "Back to projects" in the settings sidebar
  useEffect(() => {
    if (orgSlug && workspace) writeLastWorkspaceSlug(orgSlug, workspace.slug);
  }, [orgSlug, workspace]);
  if (!ready) return null;
  return workspace ? <Outlet /> : <NotFoundPage />;
}

/** `/o/:orgSlug`: on to the first visible workspace; an org with none to show gets an empty state. */
export function OrgIndexPage() {
  const { t } = useTranslation('org');
  const scope = useOrgScope();
  if (scope.status !== 'ready' || !scope.org) return null;
  const first = scope.activeWorkspaces[0];
  if (first) return <Navigate to={`/o/${scope.org.slug}/w/${first.slug}`} replace />;
  const canCreate = scope.can('workspaces.manage');
  return (
    <PageLayout title={scope.org.name}>
      <EmptyState
        title={t('index.noWorkspaceTitle')}
        description={
          canCreate
            ? t('index.noWorkspaceManageHelp')
            : t('index.noWorkspaceHelp', { org: scope.org.name })
        }
        {...(canCreate ? { action: <CreateWorkspaceButton org={scope.org} /> } : {})}
      />
    </PageLayout>
  );
}
